/**
 * FileList Component
 *
 * Displays a list of selected files with status, progress, and remove functionality.
 *
 * @module components/FileList
 */

import React from 'react';
import { FileText, X, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { FileListProps, LocalFile } from '@/features/upload/types/upload.types';
import {
  formatFileSize,
  UPLOAD_FILE_STATUS_LABELS,
  UPLOAD_FILE_STATUS_COLORS,
} from '@/features/upload/types/upload.types';

/**
 * Get icon for file status
 */
const getStatusIcon = (status?: LocalFile['status']) => {
  switch (status) {
    case 'uploading':
    case 'validating':
      return <Loader2 className="h-4 w-4 animate-spin text-info" />;
    case 'completed':
      return <CheckCircle2 className="h-4 w-4 text-success" />;
    case 'failed':
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    case 'cancelled':
      return <X className="h-4 w-4 text-muted-foreground" />;
    default:
      return <FileText className="h-4 w-4 text-muted-foreground" />;
  }
};

/**
 * FileList Component
 *
 * Displays selected files with status, progress, and remove buttons.
 *
 * @example
 * ```tsx
 * <FileList
 *   files={selectedFiles}
 *   onRemove={(fileId) => removeFile(fileId)}
 *   disabled={isUploading}
 * />
 * ```
 */
export const FileList: React.FC<FileListProps> = ({
  files,
  onRemove,
  disabled = false,
  className,
}) => {
  if (files.length === 0) {
    return null;
  }

  /**
   * Handle remove file
   */
  const handleRemove = (fileId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onRemove?.(fileId);
  };

  /**
   * Check if file can be removed
   */
  const canRemove = (file: LocalFile) => {
    return !disabled && file.status !== 'uploading' && file.status !== 'validating';
  };

  return (
    <div className={cn('space-y-2', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">
          Selected Files ({files.length})
        </h3>
        {onRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (disabled) return;
              files.forEach((file) => {
                if (canRemove(file)) {
                  onRemove(file.id);
                }
              });
            }}
            disabled={disabled || files.every((f) => !canRemove(f))}
            className="h-7 text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* File List */}
      <ScrollArea className="h-[300px] rounded-md border">
        <div className="p-4 space-y-3">
          {files.map((file) => (
            <div
              key={file.id}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-3 transition-colors',
                'hover:bg-accent/50',
                file.status === 'failed' && 'border-destructive/50 bg-destructive/5'
              )}
            >
              {/* Icon */}
              <div className="flex-shrink-0 mt-0.5">
                {getStatusIcon(file.status)}
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0 space-y-2">
                {/* Name and Size */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate" title={file.name}>
                      {file.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {formatFileSize(file.size)}
                      </span>
                      {file.format && (
                        <>
                          <span className="text-xs text-muted-foreground">•</span>
                          <Badge variant="outline" className="h-5 text-xs">
                            {file.format.toUpperCase()}
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Status Badge */}
                  {file.status && file.status !== 'pending' && (
                    <Badge
                      variant="secondary"
                      className={cn(
                        'flex-shrink-0',
                        UPLOAD_FILE_STATUS_COLORS[file.status]
                      )}
                    >
                      {UPLOAD_FILE_STATUS_LABELS[file.status]}
                    </Badge>
                  )}

                  {/* Remove Button */}
                  {onRemove && canRemove(file) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleRemove(file.id, e)}
                      className="h-6 w-6 p-0 flex-shrink-0"
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove file</span>
                    </Button>
                  )}
                </div>

                {/* Progress Bar */}
                {(file.status === 'uploading' || file.status === 'validating') &&
                  file.uploadProgress !== undefined && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          {file.status === 'uploading' ? 'Uploading' : 'Validating'}...
                        </span>
                        <span className="font-medium">{file.uploadProgress}%</span>
                      </div>
                      <Progress value={file.uploadProgress} className="h-1.5" />
                    </div>
                  )}

                {/* Error Message */}
                {file.status === 'failed' && file.error && (
                  <div className="flex items-start gap-2 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <p className="flex-1">{file.error}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Summary */}
      <div className="text-xs text-muted-foreground space-y-1">
        <p>
          Total size: {formatFileSize(files.reduce((sum, f) => sum + f.size, 0))}
        </p>
        {files.some((f) => f.status) && (
          <div className="flex items-center gap-4">
            {files.filter((f) => f.status === 'completed').length > 0 && (
              <span className="text-success">
                ✓ {files.filter((f) => f.status === 'completed').length} completed
              </span>
            )}
            {files.filter((f) => f.status === 'failed').length > 0 && (
              <span className="text-destructive">
                ✗ {files.filter((f) => f.status === 'failed').length} failed
              </span>
            )}
            {files.filter((f) => f.status === 'uploading' || f.status === 'validating').length > 0 && (
              <span className="text-info">
                ⟳ {files.filter((f) => f.status === 'uploading' || f.status === 'validating').length} in progress
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
